/*
    Display.js - Display utility routines
 */
import {State} from '@/paks/vu-state'
import {delay, getModels} from '@/paks/vu-app'
import {Progress} from '@/paks/vu-progress'

const MimeTypes = {
    avif: 'image/avif',
    js: 'application/javascript',
    css: 'text/css',
    heif: 'image/heif',
    html: 'text/html',
    htm: 'text/html',
    ico: 'image/x-icon',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    json: 'application/json',
    map: 'application/json',
    mjs: 'application/javascript',
    pdf: 'application/pdf',
    png: 'image/png',
    txt: 'text/plain',
    webp: 'image/webp',
    xml: 'application/xml',
}

/*
    Provision the manager display app
*/
export async function provision(options = {}) {
    const {Manager} = getModels()
    Progress.start('dialog', 'Provisioning App', {bar: true, duration: 30 * 1000})
    let manager = await Manager.get({id: options.id})
    let prior = manager.provisioned

    manager = await Manager.provision({id: manager.id, preserve: options.preserve})

    if (options.wait) {
        let deadline = Date.now() + options.wait
        while (Date.now() < deadline) {
            if (manager.provisioned > prior) {
                await State.cache.update()
                break
            }
            if (manager.error) {
                throw new Error('Cannot provision')
            }
            await delay(2000)
            manager = await Manager.get({id: manager.id}, {refresh: true, throw: false})
        }
        if (manager.provisioned == prior) {
            throw new Error('Timeout waiting for Provisioning')
        }
    }
    Progress.stop()
    return manager
}

export function getDisplay(name) {
    return State.app.fullDisplay.displays.find((d) => d.name == name)
}

/*
    Return {views, key, view} in the State.app.display.views
    NOTE: State.app.display must always be a direct reference to the fullDisplay.displays[item]
*/
export function getView(path, display = State.app.display) {
    for (let [key, view] of Object.entries(display.views || [])) {
        if (view.path == path) {
            return {views: display.views, key, view}
        }
        if (view.views) {
            let found = getView(path, view)
            if (found.key >= 0) {
                return found
            }
            if (found.views) {
                return found
            }
            if (path.indexOf(view.path) == 0) {
                return {views: view.views, key: -1}
            }
        }
    }
    return {}
}

export function getViewChildren(views, result = []) {
    if (!views) {
        return result
    }
    for (let view of views) {
        result.push(view)
        if (view.views) {
            getViewChildren(view.views, result)
        }
    }
    return result
}

export async function removeDisplay(display) {
    let full = State.app.fullDisplay
    let index = full.displays.findIndex((d) => d.name == display.name)
    if (index >= 0) {
        full.displays.splice(index, 1)
    }
    if (display.name == State.app.display.name) {
        display = State.app.pickDisplay(full)
        State.app.setDisplay(display)
    }
    return await persistDisplay()
}

export async function removeView(view) {
    let found = getView(view.path)
    if (found.key >= 0) {
        found.views.splice(found.key, 1)
    }
    return await persistDisplay()
}

export async function updateDisplay(display) {
    let full = State.app.fullDisplay
    let index = full.displays.findIndex((d) => d.name == display.name)
    if (index >= 0) {
        full.displays[index] = display
    } else {
        full.displays.push(display)
    }
    if (display.name == State.app.display.name) {
        State.app.setDisplay(display)
    }
    return await persistDisplay()
}

export async function updateView(view) {
    let {views, key} = getView(view.path)
    if (key >= 0) {
        views[key] = view
    } else if (views) {
        views.push(view)
    } else {
        let display = State.app.display
        display.views[0].views.push(view)
    }
    return await persistDisplay()
}

export async function persistDisplay() {
    Progress.start('short')
    const {Manager} = getModels()
    let fullDisplay = State.app.fullDisplay
    let manager = await Manager.updateDisplay({
        id: State.app.manager.id,
        display: JSON.stringify(fullDisplay),
    })
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    //  Ensure routes are updated
    State.app.setFullDisplay(fullDisplay)
    Progress.stop()
    return manager
}

/*
    Upload the assets to the app
 */
export async function uploadAsset(manager, filename, data) {
    const {Manager} = getModels()
    Progress.start('dialog', `Upload ${filename}`, {bar: true, duration: 30 * 1000})
    Progress.message('Uploading', filename)
    await uploadFile(manager, filename, data)
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    Progress.stop()
}

export async function uploadZip(manager, list, preserve = []) {
    const {Manager} = getModels()
    Progress.start('dialog', 'Upload Assets', {bar: true, duration: 30 * 1000})
    try {
        for (let [filename, file] of Object.entries(list.files)) {
            if (file.dir || preserve.find(p => filename.indexOf(p) == 0)) {
                continue
            }
            if (file.dir) continue
            Progress.message('Uploading', filename)
            let data = await file.async('blob')
            await uploadFile(manager, filename, data)
        }
    } catch (err) {
        throw err
    } finally {
        Progress.stop()
    }
    //  Trigger an invalidation
    await Manager.getSignedUrl({id: manager.id})
    return manager
}

async function uploadFile(manager, filename, data) {
    const {Manager} = getModels()
    let mimeType = getMimeType(filename)
    let url = await Manager.getSignedUrl({
        id: manager.id,
        filename,
        mimeType,
        command: 'put',
    })
    let response = await fetch(url, {
        method: 'PUT',
        body: data,
        headers: {'Content-Type': mimeType},
    })
    if (response.status != 200) {
        console.log(filename)
        console.error(response)
        throw new Error(`Cannot upload ${filename}`)
    }
}

function getMimeType(file) {
    let ext = file.match(/\.([0-9a-zA-Z]+)+$/i)
    if (!ext) {
        return 'application/octet-stream'
    }
    return MimeTypes[ext[1]]
}